import React, { useEffect } from 'react';
import { Key, Lock, Shield, Settings, FileKey } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TemplateFormData, AuthConfig } from '../TemplateWizard';

interface AuthConfigStepProps {
  data: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  onValidate: (isValid: boolean) => void;
}

const authTypes = [
  { 
    value: 'BEARER', 
    label: 'Bearer Token', 
    icon: Key, 
    description: 'Token no header Authorization' 
  },
  { 
    value: 'BASIC', 
    label: 'Basic Auth', 
    icon: Lock, 
    description: 'Username e password em Base64' 
  },
  { 
    value: 'API_KEY', 
    label: 'API Key', 
    icon: FileKey, 
    description: 'Chave em header customizado' 
  },
  { 
    value: 'OAUTH2', 
    label: 'OAuth 2.0', 
    icon: Shield, 
    description: 'Client credentials flow' 
  },
  { 
    value: 'CUSTOM_HEADER', 
    label: 'Header Customizado', 
    icon: Settings, 
    description: 'Header e valor personalizados' 
  },
] as const;

export const AuthConfigStep: React.FC<AuthConfigStepProps> = ({
  data,
  onChange,
  onValidate,
}) => {
  const authConfig = data.authConfig;

  // Update auth config
  const updateAuthConfig = (updates: Partial<AuthConfig>) => {
    onChange({
      authConfig: { ...authConfig, ...updates },
    });
  };

  // Validate on data change
  useEffect(() => {
    let isValid = !!authConfig.type;

    if (authConfig.type === 'API_KEY' || authConfig.type === 'CUSTOM_HEADER') {
      isValid = isValid && !!authConfig.headerName?.trim();
    }

    if (authConfig.type === 'OAUTH2') {
      isValid = isValid && !!(
        authConfig.oauth2Config?.tokenUrl?.trim() &&
        authConfig.oauth2Config?.clientIdField?.trim() &&
        authConfig.oauth2Config?.clientSecretField?.trim()
      );
    }

    onValidate(isValid);
  }, [authConfig, onValidate]);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Configuração de Autenticação</h2>
        <p className="text-sm text-surface-400">
          Defina como o sistema deve autenticar nas requisições ao provider
        </p>
      </div>

      {/* Auth Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-surface-200">
          Tipo de Autenticação <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {authTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = authConfig.type === type.value;
            
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => updateAuthConfig({ 
                  type: type.value as any,
                  // Reset fields when changing type
                  headerName: type.value === 'BEARER' ? 'Authorization' : '',
                  headerPrefix: type.value === 'BEARER' ? 'Bearer' : '',
                  oauth2Config: type.value === 'OAUTH2' ? {
                    tokenUrl: '',
                    clientIdField: 'clientId',
                    clientSecretField: 'clientSecret',
                    scope: '',
                  } : undefined,
                })}
                className={cn(
                  'flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left',
                  isSelected
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-600 bg-surface-800 hover:border-surface-500'
                )}
              >
                <Icon className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isSelected ? 'text-primary-400' : 'text-surface-400'
                )} />
                <div>
                  <span className={cn(
                    'text-sm font-medium block',
                    isSelected ? 'text-white' : 'text-surface-300'
                  )}>
                    {type.label}
                  </span>
                  <span className="text-xs text-surface-500">{type.description}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bearer Token Config */}
      {authConfig.type === 'BEARER' && (
        <div className="space-y-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-surface-200">Configuração Bearer Token</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-surface-300">
                Nome do Header
              </label>
              <input
                type="text"
                value={authConfig.headerName || 'Authorization'}
                onChange={(e) => updateAuthConfig({ headerName: e.target.value })}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-surface-300">
                Prefixo
              </label>
              <input
                type="text"
                value={authConfig.headerPrefix || 'Bearer'}
                onChange={(e) => updateAuthConfig({ headerPrefix: e.target.value })}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <div className="p-3 bg-surface-900 rounded-lg">
            <p className="text-xs text-surface-400">Preview do header:</p>
            <code className="text-xs text-primary-400">
              {authConfig.headerName || 'Authorization'}: {authConfig.headerPrefix || 'Bearer'} {'<token>'}
            </code>
          </div>
        </div>
      )}

      {/* API Key Config */}
      {authConfig.type === 'API_KEY' && (
        <div className="space-y-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-surface-200">Configuração API Key</h3>
          
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Nome do Header <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={authConfig.headerName || ''}
              onChange={(e) => updateAuthConfig({ headerName: e.target.value })}
              placeholder="Ex: X-API-Key, Authorization"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Prefixo (opcional)
            </label>
            <input
              type="text"
              value={authConfig.headerPrefix || ''}
              onChange={(e) => updateAuthConfig({ headerPrefix: e.target.value })}
              placeholder="Ex: Bearer, ApiKey"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="p-3 bg-surface-900 rounded-lg">
            <p className="text-xs text-surface-400">Preview do header:</p>
            <code className="text-xs text-primary-400">
              {authConfig.headerName || 'X-API-Key'}: {authConfig.headerPrefix ? `${authConfig.headerPrefix} ` : ''}{'<api_key>'}
            </code>
          </div>
        </div>
      )}

      {/* Basic Auth Config */}
      {authConfig.type === 'BASIC' && (
        <div className="space-y-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-surface-200">Configuração Basic Auth</h3>
          
          <div className="p-3 bg-surface-900 rounded-lg">
            <p className="text-xs text-surface-400 mb-2">
              O sistema irá codificar username:password em Base64 automaticamente.
            </p>
            <code className="text-xs text-primary-400">
              Authorization: Basic {'<base64(username:password)>'}
            </code>
          </div>

          <p className="text-xs text-surface-500">
            Configure os campos de username e password no próximo passo (Schema de Credenciais).
          </p>
        </div>
      )}

      {/* OAuth2 Config */}
      {authConfig.type === 'OAUTH2' && (
        <div className="space-y-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-surface-200">Configuração OAuth 2.0</h3>
          
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Token URL <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              value={authConfig.oauth2Config?.tokenUrl || ''}
              onChange={(e) => updateAuthConfig({ 
                oauth2Config: { 
                  ...authConfig.oauth2Config!, 
                  tokenUrl: e.target.value 
                } 
              })}
              placeholder="https://api.provider.com/oauth/token"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-surface-300">
                Campo Client ID <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={authConfig.oauth2Config?.clientIdField || 'clientId'}
                onChange={(e) => updateAuthConfig({ 
                  oauth2Config: { 
                    ...authConfig.oauth2Config!, 
                    clientIdField: e.target.value 
                  } 
                })}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-surface-300">
                Campo Client Secret <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={authConfig.oauth2Config?.clientSecretField || 'clientSecret'}
                onChange={(e) => updateAuthConfig({ 
                  oauth2Config: { 
                    ...authConfig.oauth2Config!, 
                    clientSecretField: e.target.value 
                  } 
                })}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Scope (opcional)
            </label>
            <input
              type="text"
              value={authConfig.oauth2Config?.scope || ''}
              onChange={(e) => updateAuthConfig({ 
                oauth2Config: { 
                  ...authConfig.oauth2Config!, 
                  scope: e.target.value 
                } 
              })}
              placeholder="read write"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Custom Header Config */}
      {authConfig.type === 'CUSTOM_HEADER' && (
        <div className="space-y-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-surface-200">Configuração Header Customizado</h3>
          
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Nome do Header <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={authConfig.headerName || ''}
              onChange={(e) => updateAuthConfig({ headerName: e.target.value })}
              placeholder="Ex: X-Custom-Auth"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-surface-300">
              Prefixo (opcional)
            </label>
            <input
              type="text"
              value={authConfig.headerPrefix || ''}
              onChange={(e) => updateAuthConfig({ headerPrefix: e.target.value })}
              placeholder="Ex: Token"
              className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="p-3 bg-surface-900 rounded-lg">
            <p className="text-xs text-surface-400">Preview do header:</p>
            <code className="text-xs text-primary-400">
              {authConfig.headerName || 'X-Custom-Auth'}: {authConfig.headerPrefix ? `${authConfig.headerPrefix} ` : ''}{'<value>'}
            </code>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthConfigStep;
